import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  LinearProgress,
  Button,
  Menu,
  MenuItem,
  Snackbar,
  Alert as MuiAlert,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon, DriveFileMove as MoveIcon, SwapHoriz as SwapHorizIcon, Add as AddIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { colors } from '../styles';
import { format as formatDate } from 'date-fns';
import { reallocateBudgetCategoryTransactions } from '../services/api';
import { showsCategoryAssignSpend } from '../utils/categoryAssign';
import CategoryAssignDialog from './CategoryAssignDialog';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& .MuiTableCell-root': {
    borderColor: `${colors.primary}10`,
    padding: theme.spacing(2),
    '&:first-of-type': {
      paddingLeft: theme.spacing(3)
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(3)
    }
  },
  '& .MuiTableCell-head': {
    backgroundColor: colors.white,
    fontWeight: 600,
    color: 'text.secondary',
    fontSize: '0.875rem',
    borderBottom: `2px solid ${colors.primary}20`
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: `${colors.primary}05`,
  }
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: `${colors.primary}20`,
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    backgroundColor: props => 
      props.value > 100 ? colors.status.error : colors.primary
  }
}));

const BudgetCategoryDetailsModal = ({
  open,
  onClose,
  category,
  budget,
  insights,
  selectedMonth,
  budgets,
  onReallocated,
  onAssigned,
  onSpend
}) => {
  const [moveAnchorEl, setMoveAnchorEl] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [reallocating, setReallocating] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [allocatedOverride, setAllocatedOverride] = useState(null);

  useEffect(() => {
    setAllocatedOverride(null);
  }, [category?._id]);

  if (!category || !insights) return null;

  // Get the correct budget if not provided directly
  const currentBudget = budget || budgets?.find(b => b._id === category.budgetId);
  if (!currentBudget) return null;

  // Get insights for the current budget
  const budgetInsights = insights.budgetInsights?.[currentBudget._id];
  if (!budgetInsights) return null;

  const categorySpent = budgetInsights.categorySpent?.[category._id] || 0;
  const categoryAmount = allocatedOverride ?? category.amount;
  const budgetAmount = category.isPercentage && allocatedOverride == null
    ? (Number(categoryAmount) / 100) * Number(currentBudget.totalBudget)
    : Number(categoryAmount);
  const hasPositiveBudgetAmount = budgetAmount > 0;
  const spentPercentage = hasPositiveBudgetAmount
    ? (categorySpent / budgetAmount) * 100
    : categorySpent > 0
      ? 100
      : 0;
  const remaining = hasPositiveBudgetAmount
    ? budgetAmount - categorySpent
    : -categorySpent;
  const transactions = budgetInsights.transactions?.filter(t => {
    const categoryId = String(category._id);
    if (t.budgetCategory?.id && String(t.budgetCategory.id) === categoryId) return true;
    return (t.allocations || []).some(
      (alloc) => alloc?.categoryId && String(alloc.categoryId) === categoryId
    );
  }) || [];

  const otherCategories = (currentBudget.categories || []).filter(
    c => c._id !== category._id
  );

  const handleConfirmReallocate = async () => {
    if (!confirmTarget) return;
    setReallocating(true);
    try {
      const result = await reallocateBudgetCategoryTransactions(
        currentBudget._id,
        category._id,
        confirmTarget._id
      );
      // Prefer the server-provided de-duplicated total so transactions touched
      // by both the legacy and allocations updates aren't counted twice. Fall
      // back to the legacy field for older backends.
      const total = typeof result?.data?.totalTransactionsUpdated === 'number'
        ? result.data.totalTransactionsUpdated
        : (result?.data?.legacyTransactionsUpdated || 0);
      setSnackbar({
        severity: 'success',
        message:
          total === 0
            ? 'No transactions needed to move.'
            : `Moved ${total} ${total === 1 ? 'transaction' : 'transactions'} to "${confirmTarget.name}".`
      });
      setConfirmTarget(null);
      if (typeof onReallocated === 'function') onReallocated(result);
    } catch (error) {
      setSnackbar({
        severity: 'error',
        message: error?.response?.data?.error || error?.message || 'Failed to move transactions'
      });
    } finally {
      setReallocating(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          backgroundColor: colors.white,
          width: '800px'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {category.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentBudget.name} • {selectedMonth ? formatDate(new Date(`${selectedMonth}-15`), 'MMMM yyyy') : 'Current Month'}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: `${colors.primary}10`
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        {/* Budget Progress Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              ${categorySpent.toLocaleString()} spent of ${budgetAmount.toLocaleString()} budget
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: spentPercentage > 100 ? colors.status.error : colors.primary,
                fontWeight: 500
              }}
            >
              {spentPercentage.toFixed(1)}%
            </Typography>
          </Box>
          <ProgressBar 
            variant="determinate" 
            value={Math.min(spentPercentage, 100)} 
          />
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: 2,
            mb: 3
          }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Remaining
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: remaining < 0 ? colors.status.error : colors.status.ready
                }}
              >
                ${Math.abs(remaining).toLocaleString()}
                {remaining < 0 && ' over'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Daily Average
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ${(categorySpent / 30).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Transactions Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Recent Transactions
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {transactions.length} {transactions.length === 1 ? 'transaction' : 'transactions'}
            </Typography>
            {transactions.length > 0 && otherCategories.length > 0 && (
              <Button
                size="small"
                startIcon={<MoveIcon />}
                onClick={(e) => setMoveAnchorEl(e.currentTarget)}
                sx={{ textTransform: 'none' }}
                aria-label="Move all transactions to another category"
              >
                Move all
              </Button>
            )}
          </Box>
        </Box>
        <Menu
          anchorEl={moveAnchorEl}
          open={Boolean(moveAnchorEl)}
          onClose={() => setMoveAnchorEl(null)}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Move to category…
            </Typography>
          </Box>
          {otherCategories.map(cat => (
            <MenuItem
              key={cat._id}
              onClick={() => {
                setMoveAnchorEl(null);
                setConfirmTarget(cat);
              }}
            >
              {cat.name}
            </MenuItem>
          ))}
        </Menu>
        <StyledTableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: 400,
            boxShadow: 'none',
            border: `1px solid ${colors.primary}10`,
            borderRadius: '12px',
            backgroundColor: 'transparent',
            width: '100%',
            overflowX: 'hidden'
          }}
        >
          <Table stickyHeader size="medium" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell width="100px">Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell width="150px">Merchant</TableCell>
                <TableCell width="100px" align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow 
                  key={transaction._id}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(new Date(transaction.authorizedDate || transaction.date), 'MMM d')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap title={transaction.description}>
                      {transaction.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap color="text.secondary">
                      {transaction.merchantName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        color: transaction.amount > 0 ? colors.status.error : colors.status.ready,
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {transaction.amount > 0 ? '-' : ''}${Math.abs(transaction.amount).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {transactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      No transactions for this period
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </DialogContent>

      {showsCategoryAssignSpend(Boolean(category._id)) && (
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<SwapHorizIcon />}
            onClick={() => setAssignOpen(true)}
            sx={{
              bgcolor: colors.primary,
              textTransform: 'none',
              borderRadius: 999,
              height: 54,
              fontWeight: 600,
              '&:hover': { bgcolor: colors.primary },
            }}
          >
            Assign
          </Button>
          <Button
            variant="contained"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => {
              if (typeof onSpend === 'function') onSpend(category);
            }}
            sx={{
              bgcolor: colors.primary,
              textTransform: 'none',
              borderRadius: 999,
              height: 54,
              fontWeight: 600,
              '&:hover': { bgcolor: colors.primary },
            }}
          >
            Spend
          </Button>
        </DialogActions>
      )}

      <CategoryAssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        budgetId={currentBudget._id}
        categoryId={category._id}
        categoryName={category.name}
        onAssigned={(result) => {
          const allocated = result?.data?.target?.allocated;
          if (allocated != null) setAllocatedOverride(Number(allocated));
          if (result?.data?.toast) {
            setSnackbar({ severity: 'success', message: result.data.toast });
          }
          if (typeof onAssigned === 'function') onAssigned(result);
        }}
      />

      <Dialog
        open={Boolean(confirmTarget)}
        onClose={() => (reallocating ? null : setConfirmTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Move all transactions?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Move every transaction in <strong>{category.name}</strong> to{' '}
            <strong>{confirmTarget?.name}</strong>. The <strong>{category.name}</strong>{' '}
            category will remain.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={reallocating}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReallocate}
            disabled={reallocating}
            variant="contained"
            startIcon={reallocating ? <CircularProgress size={16} color="inherit" /> : <MoveIcon />}
          >
            {reallocating ? 'Moving…' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <MuiAlert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </MuiAlert>
        ) : undefined}
      </Snackbar>
    </Dialog>
  );
};

export default BudgetCategoryDetailsModal;
